import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest, getRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Vendor Search Modal Component
function VendorSearchModal({ show, onHide, authToken, onVendorAdd, selectedVendors }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isVendorAdded = (vendorId) => {
    return selectedVendors.some(v => v._id === vendorId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onVendorAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Vendor</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isVendorAdded(customer._id)}
                          onClick={() => {
                            onVendorAdd(customer);
                            onHide();
                          }}
                        >
                          {isVendorAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function SaleDetails() {
  const navigate = useNavigate();
  const { id } = useParams(); // This will get the lead_id from the route
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const authCtx = useContext(AuthContext);

  // Get basic form data from navigation state (Step 1 data)
  const basicFormData = location.state?.basicFormData || null;

  const [leadData, setLeadData] = useState(null);
  const [mainLeadData, setMainLeadData] = useState(null); // Store main lead data for creating ConveyancingBranchLead
  const [vendors, setVendors] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasExistingConveyancingLead, setHasExistingConveyancingLead] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);

  // Postcode validation states
  const [postcodeValue, setPostcodeValue] = useState('');
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isPostcodeValid, setIsPostcodeValid] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      salePrice: '',
      ownershipType: 'freehold',
      lender_panel_id: '',
      partner_id: '',
      // No completionDate field for Sales

      // Info section
      sellingWithMortgage: false,
      arrangingIndemnityPolicy: false,
      unregisteredProperty: false,
      sharedOwnership: false,
      auctionRepossession: false
    }
  });

  // Ownership type options
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' }
  ];


  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // 2-step postcode validation with branch matching
  useEffect(() => {
    if (!postcodeValue || postcodeValue.trim().length === 0) {
      setIsPostcodeValid(false);
      return;
    }

    const checkPostcode = async () => {
      setCheckingPostcode(true);
      try {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return;
        }

        // STEP 2: Match branch by postcode
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (matchRes?.data?.matched) {
          setPostcodeMatch(matchRes.data.branch);
          setIsPostcodeValid(true);
        } else {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  // Fetch conveyancing lead details
  const fetchLeadDetails = async () => {
    console.log('=== FETCH LEAD DETAILS START ===');
    console.log('📍 Fetching lead with ID:', id);
    
    // Basic validation
    if (!id) {
      console.error('❌ No ID provided');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Use the correct Sale edit endpoint for getting sale conveyancing data
      let endpoint = `private/getEditDataSaleConveyancing/${id}`;
      console.log('🌐 Using API Endpoint (PRIMARY):', endpoint);
      console.log('🔍 Looking for sale data with ID:', id);

      let response;
      try {
        // First try the sale-specific endpoint
        response = await getRequest(endpoint);
        console.log('✅ API Response received from sale endpoint:', response);
      } catch (saleError) {
        console.log('❌ Sale endpoint failed, trying fallback:', saleError.message);
        // Fallback to generic conveyancing lead endpoint
        endpoint = `private/getConveyancingLead/${id}`;
        console.log('🌐 Trying fallback endpoint:', endpoint);
        response = await postRequest(endpoint, {}, authCtx.token);
        console.log('📡 API Response received from fallback:', response);
      }
      
      // Check if it's a 404 - expected for new conveyancing leads
      if (response?.status === 404) {
        console.log('📝 No existing conveyancing data found - ready for initial creation');
        setHasExistingConveyancingLead(false);
        
        // Try to fetch the main lead data to get group_code, product_id, etc.
        try {
          console.log('📊 Attempting to fetch main lead data for ID:', id);
          // COMMENTED OUT: API call causing 404 for /new routes
          // const mainLeadResponse = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);

          // if (mainLeadResponse?.status === 200 && mainLeadResponse?.data) {
          //   console.log('✅ Found main lead data:', mainLeadResponse.data);
          //   setMainLeadData(mainLeadResponse.data);

          //   // Set customers from main lead - handle both customer_ids and customers field names
          //   if (mainLeadResponse.data.customer_ids && Array.isArray(mainLeadResponse.data.customer_ids)) {
          //     setVendors(mainLeadResponse.data.customer_ids);
          //   } else if (mainLeadResponse.data.customers && Array.isArray(mainLeadResponse.data.customers)) {
          //     setVendors(mainLeadResponse.data.customers);
          //   }
          // }
        } catch (leadError) {
          console.log('⚠️ Could not fetch main lead data:', leadError);
        }

        setLoading(false);
        return; // Exit early, form will be empty for creation
      }
      
      // Handle both response.data.data and response.data formats
      const leadData = response?.data?.data || response?.data;

      if (response?.status === 200 && leadData) {
        console.log('✅ Lead data received successfully!');
        console.log('📊 Full lead data:', JSON.stringify(leadData, null, 2));
        
        setLeadData(leadData);
        setHasExistingConveyancingLead(true);
        
        // Set vendors if available - check multiple possible field names
        const vendorsData = leadData.vendors || leadData.customer_ids || leadData.customers || [];
        if (Array.isArray(vendorsData) && vendorsData.length > 0) {
          console.log('👥 Setting vendors:', vendorsData.length, 'from field:',
            leadData.vendors ? 'vendors' : leadData.customer_ids ? 'customer_ids' : 'customers');
          setVendors(vendorsData);
        } else {
          console.log('⚠️ No vendors found in lead data');
        }

        console.log('📋 Available fields in lead data:', Object.keys(leadData));

        // Auto-fill form data - with delayed execution to ensure form is ready
        // Use nextTick-like behavior with setTimeout(0) for better timing
        const fillFormData = () => {
          try {
            // Address fields
            if (leadData.property_address && typeof leadData.property_address === 'object') {
              console.log('📍 Filling address fields...');
              setValue('line1', leadData.property_address.line1 || leadData.property_address.address1 || '', { shouldValidate: false });
              setValue('line2', leadData.property_address.line2 || leadData.property_address.address2 || '', { shouldValidate: false });
              setValue('town', leadData.property_address.town || leadData.property_address.city || '', { shouldValidate: false });
              setValue('county', leadData.property_address.county || leadData.property_address.country || leadData.property_address.state || '', { shouldValidate: false });
              const prefilledPostcode = leadData.property_address.postcode || leadData.property_address.postal_code || '';
              setValue('postcode', prefilledPostcode, { shouldValidate: false });
              setPostcodeValue(prefilledPostcode); // trigger postcode validation for prefilled value
            } else if (typeof leadData.property_address === 'string') {
              const parts = leadData.property_address.split(',').map(p => p.trim());
              setValue('line1', parts[0] || '', { shouldValidate: false });
              setValue('line2', parts[1] || '', { shouldValidate: false });
              setValue('town', parts[2] || '', { shouldValidate: false });
              setValue('county', parts[3] || '', { shouldValidate: false });
              setValue('postcode', parts[parts.length - 1] || '', { shouldValidate: false });
              setPostcodeValue(parts[parts.length - 1] || ''); // trigger postcode validation for prefilled value
            }

            // Sale Price
            const price = leadData.property_value || leadData.net_value || leadData.sale_price || '';
            console.log('💰 Setting sale price:', price);
            setValue('salePrice', price ? String(price) : '', { shouldValidate: false });

            // Ownership type
            const ownershipType = (leadData.ownership_type?.toLowerCase() === 'leasehold') ? 'leasehold' : 'freehold';
            console.log('🏗️ Setting ownership type:', ownershipType);
            setValue('ownershipType', ownershipType, { shouldValidate: false });

            // Lender Panel
            const lenderPanelId = leadData.lender_panel_id
              ? (typeof leadData.lender_panel_id === 'object' ? leadData.lender_panel_id._id : leadData.lender_panel_id)
              : '';
            console.log('🏦 Lender Panel ID:', lenderPanelId);
            if (lenderPanelId) {
              setValue('lender_panel_id', String(lenderPanelId), { shouldValidate: false });
            }

            // Partner
            const partnerId = leadData.partner_id
              ? (typeof leadData.partner_id === 'object' ? leadData.partner_id._id : leadData.partner_id)
              : '';
            console.log('🤝 Partner ID:', partnerId);
            if (partnerId) {
              setValue('partner_id', String(partnerId), { shouldValidate: false });
            }

            // Info checkboxes - check multiple possible locations
            console.log('☑️ Setting info checkboxes...');
            const infoData = leadData.info || leadData.conveyancing_details || {};

            setValue('sellingWithMortgage', Boolean(
              leadData.selling_with_mortgage || infoData.sellingWithMortgage || infoData.selling_with_mortgage
            ), { shouldValidate: false });
            setValue('arrangingIndemnityPolicy', Boolean(
              leadData.arranging_indemnity_policy || infoData.arrangingIndemnityPolicy || infoData.arranging_indemnity_policy
            ), { shouldValidate: false });
            setValue('unregisteredProperty', Boolean(
              leadData.unregistered_property || infoData.unregisteredProperty || infoData.unregistered_property
            ), { shouldValidate: false });
            setValue('sharedOwnership', Boolean(
              leadData.shared_ownership || infoData.sharedOwnership || infoData.shared_ownership
            ), { shouldValidate: false });
            setValue('auctionRepossession', Boolean(
              leadData.auction_repossession || infoData.auctionRepossession || infoData.auction_repossession
            ), { shouldValidate: false });

            console.log('✅ All form fields set!');
          } catch (fillError) {
            console.error('❌ Error filling form:', fillError);
          }
        };

        // Execute form filling with proper timing
        setTimeout(fillFormData, 50);

        console.log('✅ Auto-fill process initiated!');
        console.log('=== AUTO-FILL END ===');
      } else {
        console.log('⚠️ No lead data found for auto-fill');
        console.log('Response status:', response?.status);
        console.log('Response data:', response?.data);
        console.log('=== AUTO-FILL END (NO DATA) ===');
        
        // Don't show error - this is expected for new records
        console.log('📝 Form is ready for initial data entry');
      }
    } catch (error) {
      console.error('❌ Error in auto-fill:', error);
      console.log('=== AUTO-FILL END (ERROR) ===');
      
      // Check if it's a 404 error (expected for new records)
      const is404 = error.response?.status === 404 || 
                    error.status === 404 || 
                    error.message?.includes('404') ||
                    error.response?.data?.status === 404;
      
      if (!is404) {
        // Only show error for non-404 errors
        Swal.fire({
          icon: 'error',
          title: 'Error Loading Data',
          text: 'Failed to load conveyancing data. Please refresh and try again.'
        });
      } else {
        console.log('📝 No existing data found - ready for initial creation');
        console.log('ℹ️ This is expected for new conveyancing leads');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🚀 SaleDetails useEffect triggered');
    console.log('   - id from URL:', id);
    console.log('   - authCtx.token exists:', !!authCtx.token);
    console.log('   - Current URL:', window.location.pathname);

    // Skip fetching if id is "new" - this is for creating a brand new lead
    if (id === 'new') {
      console.log('📝 Creating new conveyancing lead - skipping fetch');
      setLoading(false);
      return;
    }

    if (id && authCtx.token) {
      console.log('✅ Calling fetchLeadDetails for auto-fill...');
      console.log('   - Lead ID to fetch:', id);
      fetchLeadDetails();
    } else {
      console.log('❌ Cannot fetch - missing id or token');
      if (!id) console.log('   - Missing ID from URL params');
      if (!authCtx.token) console.log('   - Missing auth token');
    }
  }, [id, authCtx.token]);

  // Fetch customer from URL param and auto-populate in Vendors
  useEffect(() => {
    // Support both 'customerId' and 'customer_id' parameter names
    const customerId = searchParams.get('customerId') || searchParams.get('customer_id');
    if (customerId && authCtx.token && vendors.length === 0) {
      const fetchCustomer = async () => {
        try {
          // Use same endpoint as other components (supports both MongoDB ID and custom ID)
          const response = await postRequest(`private/getCustomerIndividualData/${customerId}`, {}, authCtx.token);

          if (response?.status === 200 && response?.data) {
            console.log('✅ Customer fetched from URL param:', response.data);
            setVendors([response.data]);
          }
        } catch (error) {
          console.error('Error fetching customer:', error);
        }
      };

      fetchCustomer();
    }
  }, [searchParams, authCtx.token]);

  // Fetch active lender panels on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  // Debug: Track form values
  useEffect(() => {
    const values = watch();
    console.log('📝 Form values updated:', {
      address: `${values.line1}, ${values.town}, ${values.postcode}`,
      price: values.salePrice,
      ownership: values.ownershipType,
      hasData: !!(values.line1 || values.salePrice)
    });
  }, [watch()]);

  const handleAddVendor = (customer) => {
    if (!vendors.some(v => v._id === customer._id)) {
      setVendors([...vendors, customer]);
    }
  };

  const handleRemoveVendor = (customerId) => {
    setVendors(vendors.filter(v => v._id !== customerId));
  };

  const onSubmit = async (formData) => {
    console.log('🚀 Form submitted with data:', formData);
    console.log('👥 Current vendors:', vendors);
    console.log('📍 Lead ID from URL:', id);
    console.log('📊 Existing lead data:', leadData);

    // Validate we have ID
    if (!id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No lead ID found. Please check the URL.'
      });
      return;
    }

    if (vendors.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one vendor'
      });
      return;
    }

    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    // Handle "new" case - we'll pass "new" as the lead_id to the backend
    // The backend will create the lead and trigger email automation
    let actualLeadId = id;

    if (id === 'new') {
      console.log('🆕 Creating new lead through saveConveyancingSaleToLead...');

      // Check if we have Step 1 data
      if (!basicFormData) {
        console.warn('⚠️ No Step 1 data found. Using defaults.');
      }

      // We'll use "new" as the ID, and the backend will handle creation
      actualLeadId = 'new';
    }

    // Determine if we're creating initial sale details or updating existing ones
    const isInitialCreate = !hasExistingConveyancingLead;
    console.log('🔍 Mode:', isInitialCreate ? 'INITIAL CREATE' : 'UPDATE EXISTING');
    console.log('🔄 Processing conveyancing lead with ID:', id);
    console.log('📊 Has existing conveyancing lead:', hasExistingConveyancingLead);
    console.log('📊 Lead data exists:', !!leadData);
    console.log('📊 Lead data has lead_id:', leadData?.lead_id);
    
    // Prepare update payload
    const updatePayload = {
      // Property fields
      property_address: {
        address1: formData.line1,
        address2: formData.line2,
        town: formData.town,
        country: formData.county, // Backend expects 'country'
        postcode: formData.postcode
      },
      property_value: parseFloat(formData.salePrice) || 0,
      ownership_type: formData.ownershipType === 'leasehold' ? 'Leasehold' :
                      formData.ownershipType === 'share_of_freehold' ? 'Share of Freehold' : 'Freehold',
      // No completion_date for Sales

      // Customer fields
      customer_ids: vendors.map(v => v._id || v),

      // Lender Panel
      lender_panel_id: formData.lender_panel_id || null,
      // Partner
      partner_id: formData.partner_id || null,

      // Info fields - all checkboxes for sale
      selling_with_mortgage: formData.sellingWithMortgage,
      arranging_indemnity_policy: formData.arrangingIndemnityPolicy,
      unregistered_property: formData.unregisteredProperty,
      shared_ownership: formData.sharedOwnership,
      auction_repossession: formData.auctionRepossession,

      // Additional required fields
      acquisition: mainLeadData?.acquisition || basicFormData?.acquisition || 'Direct',
      override_date: basicFormData?.override_date || mainLeadData?.override_date || new Date(),
      service_type: 'Sale', // Always Sale for this form

      // Required fields for new lead creation
      group_code: mainLeadData?.group_code || basicFormData?.group_code,
      product_id: mainLeadData?.product_id || basicFormData?.product_id,
      created_id: mainLeadData?.created_id || basicFormData?.created_id || localStorage.getItem('id')
    };
    
    // If this is an initial create and we have main lead data, add required fields
    if (isInitialCreate) {
      console.log('📋 Initial create mode. Main lead data:', mainLeadData);
      if (mainLeadData) {
        console.log('✅ Adding main lead data to payload for ConveyancingBranchLead creation');
        console.log('   - group_code:', mainLeadData.group_code);
        console.log('   - product_id:', mainLeadData.product_id);
        console.log('   - created_id:', mainLeadData.created_id);
        
        updatePayload.group_code = mainLeadData.group_code?._id || mainLeadData.group_code;
        updatePayload.product_id = mainLeadData.product_id?._id || mainLeadData.product_id;
        updatePayload.created_id = mainLeadData.created_id?._id || mainLeadData.created_id || localStorage.getItem('id');
        updatePayload.branch_id = mainLeadData.branch_id?._id || mainLeadData.branch_id;
        
        // Use acquisition from main lead if available
        if (mainLeadData.acquisition) {
          updatePayload.acquisition = mainLeadData.acquisition;
        }
        
        // Use override_date from main lead if available
        if (mainLeadData.override_date) {
          updatePayload.override_date = mainLeadData.override_date;
        }
        
        // Use status from main lead if available
        if (mainLeadData.status) {
          updatePayload.status = mainLeadData.status;
        }
        
        // Use sub_status from main lead if available
        if (mainLeadData.sub_status) {
          updatePayload.sub_status = mainLeadData.sub_status;
        }
      } else {
        console.log('⚠️ No main lead data available, trying to get minimal required fields');
        
        // Try to at least get the created_id from localStorage
        const storedCreatedId = localStorage.getItem('id');
        if (storedCreatedId) {
          updatePayload.created_id = storedCreatedId;
        }
        
        // Let's also try to fetch the lead synchronously
        // COMMENTED OUT: API call causing 404 for /new routes
        // try {
        //   console.log('🔄 Attempting synchronous fetch of main lead for ID:', id);
        //   const response = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);
        //   if (response?.status === 200 && response?.data) {
        //     console.log('✅ Got lead data in submit:', response.data);
        //     updatePayload.group_code = response.data.group_code?._id || response.data.group_code;
        //     updatePayload.product_id = response.data.product_id?._id || response.data.product_id;
        //     updatePayload.created_id = response.data.created_id?._id || response.data.created_id || storedCreatedId;

        //     // Copy additional fields from the fetched lead
        //     if (response.data.acquisition) {
        //       updatePayload.acquisition = response.data.acquisition;
        //     }
        //     if (response.data.override_date) {
        //       updatePayload.override_date = response.data.override_date;
        //     }
        //     if (response.data.status) {
        //       updatePayload.status = response.data.status;
        //     }
        //     if (response.data.sub_status) {
        //       updatePayload.sub_status = response.data.sub_status;
        //     }
        //   }
        // } catch (error) {
        //   console.error('❌ Failed to fetch lead in submit:', error);
        // }
      }
    }
    
    console.log('📤 Sending payload:', updatePayload);
    
    try {
      let response;
      
      // Always use the new endpoint that saves directly to leads database
      console.log('📝 Saving conveyancing sale data to main lead...');
      console.log('📌 API Endpoint:', `/api/v1/admin/private/saveConveyancingSaleToLead/${actualLeadId}`);
      console.log('🔑 Token exists:', !!authCtx.token);
      console.log('📦 Payload size:', JSON.stringify(updatePayload).length, 'characters');

      response = await postRequest(
        `private/saveConveyancingSaleToLead/${actualLeadId}`,
        updatePayload,
        authCtx.token
      );
      
      console.log('📡 Response:', response);
      console.log('Response status:', response?.status);
      console.log('Response message:', response?.message);
      console.log('Response data:', response?.data);

      console.log('🔍 Checking response status:', {
        status: response?.status,
        isSuccess: response?.status === 'success',
        is200: response?.status === 200,
        willCallGetSaleFee: response?.status === 'success' || response?.status === 200
      });

      if (response?.status === 'success' || response?.status === 200) {
        // Get the actual lead_id from response (important for newly created leads)
        const actualLeadId = response?.data?.lead_id || id;
        console.log('✅ Lead saved successfully, lead_id:', actualLeadId);

        // 🎯 Call unified API to calculate and save to final_leads_calculations
        try {
          // ⏱️ Wait 300ms for database to commit flags before calculating fees
          console.log('⏳ Waiting 300ms for database to commit flags...');
          await new Promise(resolve => setTimeout(resolve, 300));

          console.log('💰 Calling calculateAndSaveLeadQuote for Sale...');

          const feeResponse = await postRequest('private/calculateAndSaveLeadQuote', {
            lead_id: actualLeadId,
            service_type: 'Sale',
            product_id: updatePayload.product_id,
            property_value: updatePayload.property_value,
            ownership_type: updatePayload.ownership_type,
            postcode: formData.postcode,
            partner_id: formData.partner_id || null
          }, authCtx.token);

          console.log('✅ calculateAndSaveLeadQuote response:', feeResponse);
        } catch (feeError) {
          console.error('⚠️ Error calling calculateAndSaveLeadQuote:', feeError);
          // Non-blocking - don't fail the main operation
        }

        console.log('✅ Showing success message...');
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: id === 'new'
            ? 'Sale lead created and email sent successfully!'
            : 'Conveyancing details updated successfully!'
        });

        // Navigate to the view page instead of leads list
        console.log('🔄 Navigating to view page...');
        navigate(`/leads/view/sale/${actualLeadId}`);
      } else {
        console.error('❌ Operation failed with response:', response);
        throw new Error(response?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('❌ Error details:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      // Check if it's a network error
      if (error.message?.includes('fetch')) {
        Swal.fire({
          icon: 'error',
          title: 'Network Error',
          text: 'Unable to connect to server. Please ensure the backend is running.'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to save conveyancing details'
        });
      }
    }
  };

  // Determine if this is initial creation or update
  const isInitialCreate = !leadData || !leadData.property_value;

  if (loading) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Leads', path: '/leads' },
            { label: 'Sale Details', path: '' },
          ]}
        />
        <br />
        <Box sx={{ p: 3 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Loading conveyancing data...
            </Typography>
            <div style={{ marginBottom: '20px' }}>
              Please wait while we fetch the existing information for lead #{id}
            </div>
            <div style={{ fontSize: '24px' }}>⏳</div>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: !leadData || !leadData.property_value ? 'Add Sale Details' : 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            {!hasExistingConveyancingLead
              ? `Add Sale Details - Conveyancing ${id}`
              : `Edit Conveyancing #${leadData?.lead_id || id} - Sale`}
          </Typography>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Sale Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Sale Details
              </Typography>

              {/* Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                    <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="town"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.town}
                        helperText={errors.town?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="postcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode"
                        variant="outlined"
                        size="small"
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          setPostcodeValue(e.target.value);
                        }}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />

                  {/* Checking postcode status */}
                  {checkingPostcode && postcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Postcode match found */}
                  {postcodeMatch && !checkingPostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Branch selection dropdown */}
                  {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedBranch || ''}
                          onChange={(e) => {
                            setSelectedBranch(e.target.value);
                            setIsPostcodeValid(true);
                          }}
                          label="Select Branch"
                        >
                          {availableBranches.map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                              {branch.name} - {branch.address?.postcode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  </Grid>

                  {/* Sale Price and Ownership Type in one row */}
                  <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Sale Price</Typography>
                      <Controller
                        name="salePrice"
                        control={control}
                        rules={{ required: 'Sale price is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            slotProps={{
                              input: {
                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                              }
                            }}
                            error={!!errors.salePrice}
                            helperText={errors.salePrice?.message}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Ownership Type</Typography>
                      <Controller
                        name="ownershipType"
                        control={control}
                        rules={{ required: 'Ownership type is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth size="small" error={!!errors.ownershipType}>
                            <Select {...field}>
                              <MenuItem value="">Select Ownership Type</MenuItem>
                              {ownershipTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.ownershipType && (
                              <Typography variant="caption" color="error">
                                {errors.ownershipType.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    
                    {/* Note: No Est. Completion Date for Sales */}
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {/* Vendors Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Vendors
            </Typography>

            <Box sx={{ mb: 3 }}>
              {vendors.length > 0 ? (
                vendors.map((vendor) => (
                  <Box
                    key={vendor._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {vendor.title} {vendor.forename} {vendor.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {vendor.email && (
                          <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                            📧 {vendor.email}
                          </Typography>
                        )}
                        {vendor.phone_number && (
                          <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                            📱 {vendor.phone_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveVendor(vendor._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    No vendors added yet. Click "Add Vendor" to add customers.
                  </Typography>
                </Box>
              )}
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => setShowVendorModal(true)}
                sx={{ color: '#6f42c1', mt: 1 }}
              >
                Add Vendor
              </Button>
            </Box>

            {/* Lender Panel Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Lender Panel
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lender_panel_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel (Optional)</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={panel._id}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Partner Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Partner
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="partner_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Info Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Info
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { name: 'sellingWithMortgage', label: 'Selling with Mortgage' },
                  { name: 'arrangingIndemnityPolicy', label: 'Arranging an Indemnity Policy' },
                  { name: 'unregisteredProperty', label: 'Unregistered Property' },
                  { name: 'sharedOwnership', label: 'Shared Ownership' },
                  { name: 'auctionRepossession', label: 'Auction / Repossession' }
                ].map((item) => (
                  <Controller
                    key={item.name}
                    name={item.name}
                    control={control}
                    render={({ field }) => (
                      <Button
                        variant="outlined"
                        onClick={() => field.onChange(!field.value)}
                        sx={{
                          borderColor: field.value ? '#6f42c1' : '#ced4da',
                          backgroundColor: field.value ? '#6f42c1' : 'white',
                          color: field.value ? 'white' : '#495057',
                          textTransform: 'none',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          px: 2,
                          py: 0.75,
                          '&:hover': {
                            backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                            borderColor: field.value ? '#5a32a3' : '#adb5bd'
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  />
                ))}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/leads/view/conveyancing/${id}`)}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  '&:hover': {
                    borderColor: '#5a6268',
                    backgroundColor: 'rgba(108, 117, 125, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingPostcode || !isPostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                {isInitialCreate ? 'Save Sale Details' : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Vendor Search Modal */}
      <VendorSearchModal
        show={showVendorModal}
        onHide={() => setShowVendorModal(false)}
        authToken={authCtx.token}
        onVendorAdd={handleAddVendor}
        selectedVendors={vendors}
      />
    </>
  );
}